/* ─────────────────────────────────────────────────────────────
 *  PostHog consent sync (RGPD / DSA / ePrivacy).
 *
 *  PostHog is NOT loaded until the user accepts the "analytics"
 *  cookie category: no script, no request to PostHog before that
 *  (Cookie Policy, Art. 2). Once loaded it starts opted out with
 *  session recording off, and is opted in right away.
 *
 *  This helper bridges the existing ConsentBanner
 *  (localStorage key `tofbox_gdpr_consent`, event
 *  `tofbox_consent_changed`) with PostHog's opt-in/opt-out API.
 * ──────────────────────────────────────────────────────────── */

import { getConsent } from '../components/ads/ConsentBanner';

const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

// PostHog's own loader snippet (formerly inline in public/index.html):
// defines a stub that queues calls and injects the real library.
const STUB_METHODS = 'init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric'.split(' ');

function loadPosthog() {
  if (window.posthog) return window.posthog; // stub or real library already in
  const ph = [];
  window.posthog = ph;
  ph._i = [];
  ph.init = function init(token, config, name) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.src = `${config.api_host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
    const first = document.getElementsByTagName('script')[0];
    first.parentNode.insertBefore(script, first);

    let target = ph;
    if (name !== undefined) target = ph[name] = [];
    else name = 'posthog';
    target.people = target.people || [];
    target.toString = (stub) => `posthog${name !== 'posthog' ? `.${name}` : ''}${stub ? '' : ' (stub)'}`;
    target.people.toString = () => `${target.toString(1)}.people (stub)`;
    STUB_METHODS.forEach((method) => {
      let obj = target;
      let fn = method;
      const parts = method.split('.');
      if (parts.length === 2) { obj = target[parts[0]]; fn = parts[1]; }
      obj[fn] = function stub(...args) { obj.push([fn, ...args]); };
    });
    ph._i.push([token, config, name]);
  };
  ph.__SV = 1;

  ph.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    opt_out_capturing_by_default: true,
    disable_session_recording: true,
    session_recording: {
      recordCrossOriginIframes: true,
      capturePerformance: false,
    },
  });
  return ph;
}

/** Push the current consent decision to PostHog. Safe to call multiple times. */
export function syncPosthogConsent() {
  if (typeof window === 'undefined') return;
  const consent = getConsent();
  const analyticsAllowed = !!(consent && consent.analytics);

  // Never loaded without consent; once loaded, a withdrawal opts it out.
  const ph = analyticsAllowed ? loadPosthog() : window.posthog;
  if (!ph || typeof ph.opt_in_capturing !== 'function') return;

  try {
    if (analyticsAllowed) {
      ph.opt_in_capturing();
      if (typeof ph.startSessionRecording === 'function') {
        ph.startSessionRecording();
      }
    } else {
      ph.opt_out_capturing();
      if (typeof ph.stopSessionRecording === 'function') {
        ph.stopSessionRecording();
      }
    }
  } catch (err) {
    // Never let analytics break the app.
    // eslint-disable-next-line no-console
    console.warn('[posthog-consent] sync failed:', err);
  }
}

let listenerAttached = false;

/** Call once at app startup. Wires up the consent-changed listener too. */
export function initPosthogConsent() {
  syncPosthogConsent();
  if (listenerAttached || typeof window === 'undefined') return;
  window.addEventListener('tofbox_consent_changed', syncPosthogConsent);
  listenerAttached = true;
}
